import { useEffect } from 'react';
import type { ReactNode } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { ErrorAlert } from '../../components/ErrorAlert';
import CloseIcon from '@mui/icons-material/Close';
import { FormProvider, useForm } from 'react-hook-form';
import type { DefaultValues, FieldValues, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { ZodType } from 'zod';

export type EntityFormDialogProps<TValues extends FieldValues> = {
  open: boolean;
  mode: 'create' | 'edit';
  title: string;
  schema: ZodType<TValues>;
  initialValues: DefaultValues<TValues>;
  onSubmit: (values: TValues) => Promise<void>;
  onClose: () => void;
  /** Children render the form fields. Wrap with <FormProvider>'s useFormContext via react-hook-form. */
  children: ReactNode;
  /** Override the submit button label. Defaults to "Create" / "Save". */
  submitLabel?: string;
};

/**
 * Add/edit dialog primitive — implements PORTAL_FORM_RULES.md §4. Wraps
 * react-hook-form + zod, surfaces server errors via the in-dialog * disables the submit button while pending, restores focus on close.
 *
 * Usage: pass form fields as children; access form state via
 * `useFormContext()` from react-hook-form.
 */
export function EntityFormDialog<TValues extends FieldValues>({
  open, mode, title, schema, initialValues, onSubmit, onClose, children, submitLabel,
}: EntityFormDialogProps<TValues>) {
  const methods = useForm<TValues>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(schema as any),
    defaultValues: initialValues,
  });

  // Reset whenever the dialog opens or the initial values change.
  useEffect(() => {
    if (open) methods.reset(initialValues);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, JSON.stringify(initialValues)]);

  const submit: SubmitHandler<TValues> = async (values) => {
    try {
      await onSubmit(values);
      onClose();
    } catch (err) {
      methods.setError('root', { message: (err as Error).message ?? 'Save failed.' });
    }
  };

  const rootError = methods.formState.errors.root?.message;
  const submitting = methods.formState.isSubmitting;
  const defaultLabel = mode === 'create' ? 'Create' : 'Save';

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(submit)}>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
            <Typography variant="h6" component="span">{title}</Typography>
            <IconButton size="small" onClick={onClose} disabled={submitting}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2}>
              {rootError && <ErrorAlert>{rootError as string}</ErrorAlert>}
              {children}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose} disabled={submitting}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={submitting}
            >
              {submitting ? 'Saving…' : (submitLabel ?? defaultLabel)}
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
}
